<template lang="pug">
    div(
        v-show="show",
        class="_bar _con",
        :class="_class",
        :style="{ width, height }",
        :dir="direction")
        slot
</template>

<script>
    import resize from '../private/resize'

    const padding = {
        top:    'paddingTop',
        bottom: 'paddingBottom',
        left:   'paddingLeft',
        right:  'paddingRight'
    };

    const dimension = {
        top:    'height',
        bottom: 'height',
        left:   'width',
        right:  'width'
    };

    /**
     * This is a fixed position view context that displays as a bar docked to one side of the browser
     * window (either top, bottom, left, or right). It should be used via this.$overlay() until the new
     * ib-dialog component is available.
     *
     * If you are using this component inline, be aware that Safari will clip it to any parent with
     * overflow: [not visible]. Therefore, it is recommended you use it near the top of your DOM
     * hierarchy.
     */

    export default {
        name: 'ib-bar',
        mixins: [resize],

        props: {
            /**
             * Dock position in window [top, bottom, left, right].
             */
            position: {
                type: String,
                default: 'top'
            },
            /**
             * Alignment relative to docked edge [start, center, end, grow].
             */
            align: {
                type: String,
                default: 'grow'
            },
            /**
             * Controls shadow; `low`, `med` or `high` supported. Default is no shadow.
             */
            elevation: String,
            /**
             * If `true`, close on click or touch anywhere outside element.
             */
            autoclose: Boolean,
            /**
             * Width in any CSS units.
             */
            width: String,
            /**
             * Height in any CSS units.
             */
            height: String
        },

        data() {
            const position = this.position;
            return {
                show: true,
                rseW: position == 'left' || position == 'right',
                rseH: position == 'top' || position == 'bottom'
            }
        },

        computed: {
            direction() {
                const app = this.$root.__app;
                return app ? app.direction : 'ltr';
            },

            _class() {
                return {
                    ['_p'+this.position]: true,
                    ['_a'+this.align]: true,
                    ['_e'+this.elevation]: this.elevation
                };
            }
        },

        watch: {
            autoclose: {
                handler(to) {
                    if (this.show) {
                        if (to) {
                            this._bodyattach();
                        } else {
                            this._bodydetach();
                        }
                    }
                },
                immediate: true
            }
        },

        beforeDestroy() {
            document.body.style[padding[this.position]] = '';
        },

        methods: {
            open() {
                if (!this.show) {
                    this.show = true;
                    if (this.autoclose) {
                        this._bodyattach();
                    }
                    this.$emit('open');
                }
            },

            close() {
                if (this.show) {
                    this.show = false;
                    if (this.autoclose) {
                        this._bodydetach();
                    }
                    this.$emit('close');
                }
            },

            toggle() {
                if (this.show) {
                    this.open();
                } else {
                    this.close();
                }
            },

            resize() {
                document.body.style[padding[this.position]] =
                    this.$el.getBoundingClientRect()[dimension[this.position]] + 'px';
            },

            _bodyattach() {
                const doc = document.documentElement;
                doc.addEventListener('mousedown', this._dismiss, true);
                doc.addEventListener('touchstart', this._dismiss, true);
            },

            _bodydetach() {
                const doc = document.documentElement;
                doc.removeEventListener('mousedown', this._dismiss, true);
                doc.removeEventListener('touchstart', this._dismiss, true);
            },

            _dismiss(e) {
                if (!e.button && !this.$util.isChildOrSelf(e.target, this.$el)) {
                    this.close();
                }
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._bar {
        position: fixed;
        max-width: 100%;
        max-height: 100%;
        z-index: @z-modeless;

        &._ptop {
            top: 0;
            animation: _bar_top .4s;
        }
        &._pbottom {
            bottom: 0;
        }
        &._pleft {
            left: 0;
        }
        &._pright {
            right: 0;
        }
        &._ptop,
        &._pbottom {
            &._astart {
                left: 0;
            }
            &._acenter {
                left: 50%;
                transform: translateX(-50%);
            }
            &._aend {
                right: 0;
            }
            &._agrow {
                left: 0;
                right: 0;
            }
        }
        &._pleft,
        &._pright {
            &._astart {
                top: 0;
            }
            &._acenter {
                top: 50%;
                transform: translateY(-50%);
            }
            &._aend {
                bottom: 0;
            }
            &._agrow {
                top: 0;
                bottom: 0;
            }
        }

        &._elow {
            z-index: @z-modeless-low;
            .shadow-low();
        }
        &._emed {
            z-index: @z-modeless-med;
            .shadow-med();
        }
        &._ehigh {
            z-index: @z-modeless-high;
            .shadow-high();
        }
    }
</style>
